import { useTranslation } from 'next-i18next';
import { SparklingFillIcon } from '@/icons';
import { Button } from '@/ui_v3';
import { cn, isIos } from '@/utils';
import { type GenerationMode } from '../types';
import { AiGradientButton } from './AiGradientButton';
import { BuildAiGenerateSplitButton } from './BuildAiGenerateSplitButton';
export type GenerateStatus = 'idle' | 'generating' | 'regenerating' | 'success' | 'error';
export type BuildGenerateActionProps = {
  onGenerate?: () => void;
  onCancel: () => void;
  status: GenerateStatus;
  className?: string;
  /** 모드 선택 split button을 사용할 때만 전달. 없으면 단일 액션 버튼으로 렌더. */
  generationMode?: GenerationMode;
  onGenerationModeChange?: (mode: GenerationMode) => void;
  /** Plan 권한 부족으로 잠긴 모드. dropdown 에 PlanBadge 가 노출된다. */
  lockedModes?: readonly GenerationMode[];
  /** 최초 생성 흐름에서 chevron coach-mark tooltip 노출 여부. */
  shouldShowModeCoachMark?: boolean;
};
export const BuildGenerateAction = ({
  className,
  onGenerate,
  onCancel,
  status,
  generationMode,
  onGenerationModeChange,
  lockedModes,
  shouldShowModeCoachMark
}: BuildGenerateActionProps) => {
  if (status === 'success' || status === 'regenerating') {
    return <ReGenerateAction onConfirm={onGenerate} isRegenerating={status === 'regenerating'} generationMode={generationMode} onGenerationModeChange={onGenerationModeChange} lockedModes={lockedModes} />;
  }
  return <GenerateAction className={cn('hidden tablet-sm:flex', className)} onCancel={onCancel} onConfirm={onGenerate} isPending={status === 'generating'} generationMode={generationMode} onGenerationModeChange={onGenerationModeChange} lockedModes={lockedModes} shouldShowModeCoachMark={shouldShowModeCoachMark} data-sentry-element="GenerateAction" data-sentry-component="BuildGenerateAction" data-sentry-source-file="BuildAiGenerateAction.tsx" />;
};
const ReGenerateAction = ({
  onConfirm,
  isRegenerating,
  generationMode,
  onGenerationModeChange,
  lockedModes
}: {
  onConfirm?: () => void;
  isRegenerating: boolean;
  generationMode?: GenerationMode;
  onGenerationModeChange?: (mode: GenerationMode) => void;
  lockedModes?: readonly GenerationMode[];
}) => {
  // 모드 컨텍스트가 있을 때는 split button (alternative tone). Figma 36615-28445.
  if (generationMode !== undefined && onGenerationModeChange) {
    return <BuildAiGenerateSplitButton className="w-full shrink-0" variant="alternative" selectedMode={generationMode} onModeChange={onGenerationModeChange} onGenerate={() => onConfirm?.()} disabled={isRegenerating || !onConfirm} isLoading={isRegenerating} lockedModes={lockedModes} />;
  }

  // 모드 컨텍스트가 없는 호출자 (예: SimilarQuizPrompt) 용 단일 액션 버튼.
  return <ReGenerateFallback onConfirm={onConfirm} isRegenerating={isRegenerating} data-sentry-element="ReGenerateFallback" data-sentry-component="ReGenerateAction" data-sentry-source-file="BuildAiGenerateAction.tsx" />;
};
const ReGenerateFallback = ({
  onConfirm,
  isRegenerating
}: {
  onConfirm?: () => void;
  isRegenerating: boolean;
}) => {
  const {
    t
  } = useTranslation(['build']);
  return <Button className="w-full shrink-0" onClick={() => onConfirm?.()} intent={'alternative'} leftSlot={<SparklingFillIcon size={'md'} />} disabled={isRegenerating || !onConfirm} data-sentry-element="Button" data-sentry-component="ReGenerateFallback" data-sentry-source-file="BuildAiGenerateAction.tsx">
      {t('ai.regenerate.button')}
    </Button>;
};
export const GenerateAction = ({
  className,
  onCancel,
  onConfirm,
  isPending,
  generationMode,
  onGenerationModeChange,
  lockedModes,
  shouldShowModeCoachMark
}: {
  className?: string;
  onCancel: () => void;
  onConfirm?: () => void;
  isPending: boolean;
  generationMode?: GenerationMode;
  onGenerationModeChange?: (mode: GenerationMode) => void;
  lockedModes?: readonly GenerationMode[];
  shouldShowModeCoachMark?: boolean;
}) => {
  const {
    t
  } = useTranslation(['common', 'build']);
  const isIosDevice = isIos();
  const hasModeSplit = generationMode !== undefined && !!onGenerationModeChange;
  return <div className={cn('flex w-full justify-end sticky gap-xs tablet-sm:pt-md tablet-sm:pb-xl bg-white', !isIosDevice && 'bottom-0 right-0 left-0', className)} data-sentry-component="GenerateAction" data-sentry-source-file="BuildAiGenerateAction.tsx">
      <Button intent={'neutral'} variant={'transparent'} onClick={onCancel} data-sentry-element="Button" data-sentry-source-file="BuildAiGenerateAction.tsx">
        {t('common:global.cancel.button')}
      </Button>
      {hasModeSplit ? <BuildAiGenerateSplitButton className="min-w-[230px] grow tablet-sm:grow-0" selectedMode={generationMode} onModeChange={onGenerationModeChange} onGenerate={() => onConfirm?.()} disabled={isPending || !onConfirm} lockedModes={lockedModes} shouldShowTooltip={shouldShowModeCoachMark} /> : <AiGradientButton className="min-w-[200px] grow tablet-sm:grow-0" onClick={() => onConfirm?.()} disabled={isPending || !onConfirm}>
          <SparklingFillIcon size={'md'} />
          {t('build:ai.generate.button2')}
        </AiGradientButton>}
    </div>;
};